import React, { useCallback, useEffect } from 'react';
import { useAppDispatch } from '../../store/hooks';
import { setUser, setLoading, setAuthReady, clearUser } from '../../store/slices/userSlice';
import authService from '../../services/authService';

/**
 * Confirms the JWT cookie against /api/auth/me on load and when the tab is
 * focused again. Server restarts drop HttpSession; /me recreates it from JWT.
 */
const AuthChecker: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const dispatch = useAppDispatch();

  const syncAuth = useCallback(async () => {
    try {
      const response = await authService.getCurrentUser();
      if (response.success && response.user) {
        dispatch(setUser(response.user));
      } else {
        dispatch(clearUser());
      }
    } catch (error: unknown) {
      const status =
        error && typeof error === 'object' && 'response' in error
          ? (error as { response?: { status?: number } }).response?.status
          : undefined;
      if (status === 401 || status === 403) {
        dispatch(clearUser());
      }
    } finally {
      dispatch(setLoading(false));
      dispatch(setAuthReady(true));
    }
  }, [dispatch]);

  useEffect(() => {
    dispatch(setLoading(true));
    syncAuth();

    const onVisible = () => {
      if (document.visibilityState === 'visible') {
        syncAuth();
      }
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [dispatch, syncAuth]);

  return <>{children}</>;
};

export default AuthChecker;
